/*
 * Copyright (c) 2026, Wultra s.r.o. (www.wultra.com).
 *
 * All rights reserved. This source code can be used only for purposes specified
 * by the given license contract signed by the rightful deputy of Wultra s.r.o.
 * This source code can be used only by the owner of the license.
 *
 * Any disputes arising in respect of this agreement (license) shall be brought
 * before the Municipal Court of Prague.
 */

import clsx from "clsx"
import { SmartphoneIcon } from "lucide-react"
import { observer } from "mobx-react-lite"

import type { ApkDetail } from "../../features/apk-detail/apk-detail-types"
import { valueOrDash } from "../../utils/value-or-dash"
import { ClipboardValue } from "../components/clipboard-value"
import { TimestampWithIcon } from "../components/timestamp-with-icon"
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "../primitives/card"

import styles from "./apk-detail-info.module.css"

interface Props {
    /**
     * APK to display.
     */
    apk: ApkDetail
}

/**
 * Displays the APK summary at the top of the detail page.
 *
 * Shows the APK identity, versions, signatures, affected device count, and
 * timestamps. Threat level, categories, permissions, and external sources are
 * shown in separate cards.
 *
 * Long values use middle truncation where appropriate so both ends remain
 * visible, while the full value is available on hover.
 *
 * @example
 * <ApkDetailInfo apk={apk} />
 */
export const ApkDetailInfo = observer(({ apk }: Props) => (
    <Card>
        <CardHeader>
            <CardTitle>
                {/* Prevent a long SDK-reported name from expanding the card. */}
                <h2 className="m-0 text-3xl font-bold wrap-anywhere">{apk.name}</h2>
            </CardTitle>
        </CardHeader>
        <CardContent className={styles.body}>
            <dl className={styles.table}>
                <dt className={styles.key}>Package:</dt>
                <dd className={clsx("font-mono", styles.value)}>
                    <ClipboardValue value={apk.packageName} />
                </dd>

                <dt className={styles.key}>Version Name:</dt>
                <dd className={styles.value}>{valueOrDash(apk.versionName)}</dd>

                <dt className={styles.key}>Version Code:</dt>
                <dd className={clsx("tabular-nums", styles.value)}>{apk.versionCode}</dd>

                <dt className={styles.key}>APK Signature (SHA-256):</dt>
                <dd className={clsx("font-mono", styles.value)}>
                    <ClipboardValue value={apk.apkSignature} />
                </dd>

                <dt className={styles.key}>Certificate Signature (SHA-1):</dt>
                <dd className={clsx("font-mono", styles.value)}>
                    <ClipboardValue value={apk.certificateSignature} />
                </dd>

                <dt className={styles.key}>Affected Devices:</dt>
                <dd className={styles.value}>
                    <span className="inline-flex items-center gap-1.5 tabular-nums">
                        <SmartphoneIcon className="size-4 text-muted-foreground" />
                        {/* The API counts only devices the user can access. */}
                        {apk.affectedDevices.toLocaleString("en-GB")}
                    </span>
                </dd>
            </dl>
        </CardContent>
        <CardFooter>
            <dl className={styles.timestamps}>
                <div>
                    <dt className={styles.key}>First Seen:</dt>
                    <dd className={styles.value}>
                        <TimestampWithIcon kind="created" timestamp={apk.timestampCreated} />
                    </dd>
                </div>

                {/* Show Updated only when the APK changed after it was first seen. */}
                {apk.timestampUpdated && (
                    <div>
                        <dt className={styles.key}>Updated:</dt>
                        <dd className={styles.value}>
                            <TimestampWithIcon kind="updated" timestamp={apk.timestampUpdated} />
                        </dd>
                    </div>
                )}
            </dl>
        </CardFooter>
    </Card>
))
